import React, { useState, useEffect } from 'react';
import { useTheme } from './context/ThemeContext';
import { FaChartLine } from 'react-icons/fa';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Animated Wave Background Component
const AnimatedWaveBackground = ({ theme }) => {
  const [nodes, setNodes] = useState([]);
  const [waveNodes, setWaveNodes] = useState([]);

  // Convert hex to HSL for color variations
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0;
      }
      h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  };

  const primaryHsl = hexToHsl(theme.primaryColor);
  const secondaryHsl = hexToHsl(theme.secondaryColor);

  useEffect(() => {
    // Generate random floating nodes
    const generateNodes = () => {
      const nodeCount = 120;
      const newNodes = [];
      
      for (let i = 0; i < nodeCount; i++) {
        const usePrimary = Math.random() > 0.5;
        const baseHsl = usePrimary ? primaryHsl : secondaryHsl;
        const hueVariation = (Math.random() - 0.5) * 60; // ±30 degree variation
        
        newNodes.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 6 + 1,
          speed: Math.random() * 0.8 + 0.3,
          direction: Math.random() * Math.PI * 2,
          opacity: Math.random() * 0.8 + 0.2,
          hue: Math.max(0, Math.min(360, baseHsl[0] + hueVariation)),
          saturation: Math.max(30, Math.min(100, baseHsl[1] + (Math.random() - 0.5) * 40)),
          lightness: Math.max(40, Math.min(90, baseHsl[2] + (Math.random() - 0.5) * 30))
        });
      }
      
      setNodes(newNodes);
    };

    // Generate wave-specific nodes that follow wave paths
    const generateWaveNodes = () => {
      const waveNodeCount = 80;
      const newWaveNodes = [];
      
      for (let i = 0; i < waveNodeCount; i++) {
        const x = (i / waveNodeCount) * 100;
        const baseY = 60 + Math.sin((i / waveNodeCount) * Math.PI * 4) * 15;
        const usePrimary = Math.random() > 0.3; // Favor primary color
        const baseHsl = usePrimary ? primaryHsl : secondaryHsl;
        const hueVariation = (Math.random() - 0.5) * 40;
        
        newWaveNodes.push({
          id: `wave-${i}`,
          x: x,
          y: baseY + Math.random() * 10 - 5,
          size: Math.random() * 4 + 2,
          phase: Math.random() * Math.PI * 2,
          amplitude: Math.random() * 20 + 10,
          frequency: Math.random() * 0.02 + 0.01,
          opacity: Math.random() * 0.6 + 0.4,
          hue: Math.max(0, Math.min(360, baseHsl[0] + hueVariation)),
          saturation: Math.max(50, Math.min(100, baseHsl[1] + (Math.random() - 0.5) * 30)),
          lightness: Math.max(50, Math.min(95, baseHsl[2] + (Math.random() - 0.5) * 20))
        });
      }
      
      setWaveNodes(newWaveNodes);
    };

    generateNodes();
    generateWaveNodes();
  }, [theme.primaryColor, theme.secondaryColor]); // Regenerate when theme colors change

  // Create gradient colors based on theme
  const createGradientStops = () => {
    const primary = theme.primaryColor;
    const secondary = theme.secondaryColor;
    
    // Create intermediate colors by blending
    const blendColors = (color1, color2, ratio) => {
      const hex1 = color1.slice(1);
      const hex2 = color2.slice(1);
      
      const r1 = parseInt(hex1.slice(0, 2), 16);
      const g1 = parseInt(hex1.slice(2, 4), 16);
      const b1 = parseInt(hex1.slice(4, 6), 16);
      
      const r2 = parseInt(hex2.slice(0, 2), 16);
      const g2 = parseInt(hex2.slice(2, 4), 16);
      const b2 = parseInt(hex2.slice(4, 6), 16);
      
      const r = Math.round(r1 + (r2 - r1) * ratio);
      const g = Math.round(g1 + (g2 - g1) * ratio);
      const b = Math.round(b1 + (b2 - b1) * ratio);
      
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };
    
    const blend1 = blendColors(primary, secondary, 0.3);
    const blend2 = blendColors(primary, secondary, 0.7);
    
    return { primary, secondary, blend1, blend2 };
  };

  const gradientColors = createGradientStops();

  return (
    <div className="wave-background">
      {/* Multiple Animated SVG Waves */}
      <svg className="wave-svg wave-primary" viewBox="0 0 1200 320" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradientColors.primary} stopOpacity="0.9">
              <animate attributeName="stop-color" 
                values={`${gradientColors.primary};${gradientColors.blend1};${gradientColors.secondary};${gradientColors.blend2};${gradientColors.primary}`}
                dur="15s" 
                repeatCount="indefinite" />
            </stop>
            <stop offset="30%" stopColor={gradientColors.blend1} stopOpacity="0.7">
              <animate attributeName="stop-color" 
                values={`${gradientColors.blend1};${gradientColors.secondary};${gradientColors.blend2};${gradientColors.primary};${gradientColors.blend1}`}
                dur="18s" 
                repeatCount="indefinite" />
            </stop>
            <stop offset="70%" stopColor={gradientColors.secondary} stopOpacity="0.6">
              <animate attributeName="stop-color" 
                values={`${gradientColors.secondary};${gradientColors.blend2};${gradientColors.primary};${gradientColors.blend1};${gradientColors.secondary}`}
                dur="20s" 
                repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor={gradientColors.blend2} stopOpacity="0.5">
              <animate attributeName="stop-color" 
                values={`${gradientColors.blend2};${gradientColors.primary};${gradientColors.blend1};${gradientColors.secondary};${gradientColors.blend2}`}
                dur="22s" 
                repeatCount="indefinite" />
            </stop>
          </linearGradient>
          
          <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradientColors.secondary} stopOpacity="0.6">
              <animate attributeName="stop-color" 
                values={`${gradientColors.secondary};${gradientColors.primary};${gradientColors.blend1};${gradientColors.blend2};${gradientColors.secondary}`}
                dur="25s" 
                repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor={gradientColors.blend1} stopOpacity="0.4">
              <animate attributeName="stop-color" 
                values={`${gradientColors.blend1};${gradientColors.blend2};${gradientColors.primary};${gradientColors.secondary};${gradientColors.blend1}`}
                dur="20s" 
                repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        
        {/* Primary Wave with complex animation */}
        <path fill="url(#waveGradient1)" fillOpacity="0.8">
          <animate attributeName="d" 
            values="M0,160L40,144C80,128,160,96,240,106.7C320,117,400,171,480,186.7C560,203,640,181,720,154.7C800,128,880,96,960,90.7C1040,85,1120,107,1160,117.3L1200,128L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,96L40,112C80,128,160,160,240,160C320,160,400,128,480,122.7C560,117,640,139,720,149.3C800,160,880,160,960,138.7C1040,117,1120,75,1160,53.3L1200,32L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,192L40,197.3C80,203,160,213,240,208C320,203,400,181,480,170.7C560,160,640,160,720,176C800,192,880,224,960,229.3C1040,235,1120,213,1160,202.7L1200,192L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,160L40,144C80,128,160,96,240,106.7C320,117,400,171,480,186.7C560,203,640,181,720,154.7C800,128,880,96,960,90.7C1040,85,1120,107,1160,117.3L1200,128L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z" 
            dur="25s" 
            repeatCount="indefinite" />
        </path>
        
        {/* Secondary Wave */}
        <path fill="url(#waveGradient2)" fillOpacity="0.6">
          <animate attributeName="d" 
            values="M0,224L40,213.3C80,203,160,181,240,181.3C320,181,400,203,480,208C560,213,640,203,720,181.3C800,160,880,128,960,128C1040,128,1120,160,1160,176L1200,192L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,256L40,245.3C80,235,160,213,240,208C320,203,400,213,480,224C560,235,640,245,720,234.7C800,224,880,192,960,181.3C1040,171,1120,181,1160,186.7L1200,192L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,192L40,202.7C80,213,160,235,240,229.3C320,224,400,192,480,181.3C560,171,640,181,720,202.7C800,224,880,256,960,261.3C1040,267,1120,245,1160,234.7L1200,224L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,224L40,213.3C80,203,160,181,240,181.3C320,181,400,203,480,208C560,213,640,203,720,181.3C800,160,880,128,960,128C1040,128,1120,160,1160,176L1200,192L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z" 
            dur="30s" 
            repeatCount="indefinite" />
        </path>
      </svg>

      {/* Third wave layer */}
      <svg className="wave-svg wave-tertiary" viewBox="0 0 1200 320" preserveAspectRatio="none">
        <path fill={`${gradientColors.primary}33`} fillOpacity="0.4">
          <animate attributeName="d" 
            values="M0,288L40,282.7C80,277,160,267,240,250.7C320,235,400,213,480,218.7C560,224,640,256,720,261.3C800,267,880,245,960,234.7C1040,224,1120,224,1160,224L1200,224L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,256L40,261.3C80,267,160,277,240,272C320,267,400,245,480,240C560,235,640,245,720,261.3C800,277,880,299,960,293.3C1040,288,1120,256,1160,240L1200,224L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z;
                     M0,288L40,282.7C80,277,160,267,240,250.7C320,235,400,213,480,218.7C560,224,640,256,720,261.3C800,267,880,245,960,234.7C1040,224,1120,224,1160,224L1200,224L1200,320L1160,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z" 
            dur="35s" 
            repeatCount="indefinite" />
        </path>
      </svg>

      {/* Regular Floating Nodes */}
      <div className="nodes-container">
        {nodes.map((node) => (
          <div
            key={node.id}
            className="floating-node"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              width: `${node.size}px`,
              height: `${node.size}px`,
              opacity: node.opacity,
              background: `hsla(${node.hue}, ${node.saturation}%, ${node.lightness}%, ${node.opacity})`,
              boxShadow: `0 0 ${node.size * 3}px hsla(${node.hue}, ${node.saturation}%, ${node.lightness}%, 0.5)`,
              animationDuration: `${25 + node.speed * 20}s`,
              animationDelay: `${node.id * 0.1}s`
            }}
          />
        ))}
      </div>

      {/* Wave-following nodes */}
      <div className="wave-nodes-container">
        {waveNodes.map((node) => (
          <div
            key={node.id}
            className="wave-node"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              width: `${node.size}px`,
              height: `${node.size}px`,
              opacity: node.opacity,
              background: `hsla(${node.hue}, ${node.saturation}%, ${node.lightness}%, ${node.opacity})`,
              boxShadow: `0 0 ${node.size * 4}px hsla(${node.hue}, ${node.saturation}%, ${node.lightness}%, 0.6)`,
              animationDuration: `${15 + node.phase * 8}s`,
              animationDelay: `${node.x * 0.15}s`
            }}
          />
        ))}
      </div>

      {/* Enhanced Connection Lines */}
      <svg className="connections-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        {nodes.slice(0, 25).map((node, index) => {
          const nextNode = nodes[(index + 1) % 25];
          const connectionOpacity = Math.sin(Date.now() * 0.001 + index) * 0.1 + 0.15;
          const connectionHue = index % 2 === 0 ? primaryHsl[0] : secondaryHsl[0];
          return (
            <line
              key={`connection-${index}`}
              x1={`${node.x}%`}
              y1={`${node.y}%`}
              x2={`${nextNode?.x || 0}%`}
              y2={`${nextNode?.y || 0}%`}
              stroke={`hsla(${connectionHue}, 70%, 80%, ${connectionOpacity})`}
              strokeWidth="0.3"
              className="connection-line"
              style={{ 
                animationDelay: `${index * 0.15}s`,
                animationDuration: `${3 + index * 0.2}s`
              }}
            />
          );
        })}
        
        {/* Wave node connections */}
        {waveNodes.slice(0, 15).map((node, index) => {
          const nextNode = waveNodes[(index + 2) % 15];
          const connectionHue = (primaryHsl[0] + secondaryHsl[0]) / 2;
          return (
            <line
              key={`wave-connection-${index}`}
              x1={`${node.x}%`}
              y1={`${node.y}%`}
              x2={`${nextNode?.x || 0}%`}
              y2={`${nextNode?.y || 0}%`}
              stroke={`hsla(${connectionHue}, 60%, 85%, 0.2)`}
              strokeWidth="0.5"
              className="wave-connection-line"
              style={{ 
                animationDelay: `${index * 0.3}s`,
                animationDuration: `${4 + index * 0.1}s`
              }}
            />
          );
        })}
      </svg>

      {/* Particle Effects */}
      <div className="particle-effects">
        {Array.from({ length: 30 }, (_, i) => (
          <div
            key={`particle-${i}`}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? `${gradientColors.primary}CC` : `${gradientColors.secondary}CC`,
              boxShadow: `0 0 6px ${i % 2 === 0 ? gradientColors.primary : gradientColors.secondary}`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>
    </div>
  );
};

function Login() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('Step 1: Starting OTP verification...');
      // Step 1: Verify OTP
      const otpResponse = await fetch('https://passwordbot-ssms-68f5130c039f.herokuapp.com/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          otp: otp
        }),
      });
      
      if (!otpResponse.ok) {
        throw new Error('Invalid OTP');
      }
      
      const otpData = await otpResponse.json();
      console.log('OTP verification response:', otpData);
      
      if (otpData.success) {
        console.log('Step 2: OTP verified, getting JWT token...');
        // Step 2: Get JWT token from backend with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        try {
          const tokenResponse = await fetch(`${API_URL}/token-otp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller.signal
          });

          clearTimeout(timeoutId);
          console.log('Token response status:', tokenResponse.status);
          
          if (!tokenResponse.ok) {
            const errorText = await tokenResponse.text();
            console.error('Token response error:', errorText);
            throw new Error(`Failed to get authentication token: ${tokenResponse.status} - ${errorText}`);
          }

          const tokenData = await tokenResponse.json();
          console.log('Token received successfully');
          localStorage.setItem('accessToken', tokenData.access_token);
          
          console.log('Step 3: Redirecting to dashboard...');
          // Refresh the page to ensure all components are re-initialized with the new token
          window.location.href = '/';
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error('Backend request timed out. Please check if the backend server is running.');
          }
          throw fetchError;
        }
      } else {
        throw new Error(otpData.message || 'OTP verification failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Verification failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 4 digits
    if (/^\d*$/.test(value) && value.length <= 4) {
      setOtp(value);
    }
  };

  return (
    <div 
      className="login-container"
      style={{
        '--primary-color': theme.primaryColor,
        '--secondary-color': theme.secondaryColor
      }}
    >
      <AnimatedWaveBackground theme={theme} />
      <div className="login-content">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <FaChartLine />
            </div>
            <h1>KPI Dashboard</h1>
            <p className="login-subtitle">Analytics & Performance Tracking</p>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="otp">Enter 4-digit OTP</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={handleOtpChange}
                maxLength={4}
                pattern="\d{4}"
                required
                placeholder="Enter 4-digit code"
                className="otp-input"
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" disabled={loading || otp.length !== 4} className="login-button">
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;